import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ThemeContext = createContext({
  theme: null,
  loading: true,
  refresh: () => {},
  applyPreview: (_themeKeyOrTokens) => {},
  clearPreview: () => {},
});

/** Check if current user is marketer from user data in localStorage */
// function isMarketerUser() {
//   try {
//     const userStr = localStorage.getItem('user');
//     if (!userStr) return false;
//     const user = JSON.parse(userStr);
//     return user.role === 'marketer';
//   } catch {
//     return false;
//   }
// }

/** Apply a theme to the document root. */
function applyThemeToDocument(theme) {
  const root = document.documentElement;
  if (!theme) return;
  // Skip theme application for marketer users
  // if (isMarketerUser()) return;
  // Built-in themes are referenced via data-theme="<key>"
  root.setAttribute('data-theme', theme.key || 'islamic_royal');
  // Also expose CSS variables from tokens for custom themes
  const t = theme.tokens || {};
  const map = {
    '--theme-primary': t.primary,
    '--theme-primary-dark': t.primary_dark,
    '--theme-primary-light': t.primary_light,
    '--theme-gold': t.gold,
    '--theme-gold-light': t.gold_light,
    '--theme-sand': t.sand,
    '--theme-ink': t.ink,
    '--theme-accent': t.accent,
    '--theme-card-bg': t.card_bg,
    '--theme-card-border': t.card_border,
    '--theme-radius': t.radius,
    '--theme-font-heading': t.font_heading,
    '--theme-font-body': t.font_body,
  };
  // For custom themes, also apply a data-theme="custom" so preset CSS doesn't override
  if (theme.category === 'custom') {
    root.setAttribute('data-theme', 'custom');
    Object.entries(map).forEach(([k, v]) => v && root.style.setProperty(k, v));
    if (t.page_bg) root.style.setProperty('--theme-page-image', t.page_bg);
    if (t.sidebar_bg) root.style.setProperty('--theme-sidebar-image', t.sidebar_bg);
  } else {
    // For preset, clear any custom inline overrides so the CSS class wins
    [
      '--theme-primary', '--theme-primary-dark', '--theme-primary-light',
      '--theme-gold', '--theme-gold-light', '--theme-sand', '--theme-ink',
      '--theme-accent', '--theme-card-bg', '--theme-card-border', '--theme-radius',
      '--theme-font-heading', '--theme-font-body',
      '--theme-page-image', '--theme-sidebar-image',
    ].forEach((k) => root.style.removeProperty(k));
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const previewRef = useRef(null);

  const location = useLocation();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '');
      // Active theme requires auth. For unauthenticated users, fall back to islamic_royal CSS class.
      if (!token) {
        document.documentElement.setAttribute('data-theme', 'islamic_royal');
        setTheme({ key: 'islamic_royal', name: 'Islamic Royal', is_builtin: true });
        return;
      }
      // Use page key from window location (first segment) — coarse but enough
      const pageKey = window.location.pathname.split('/').filter(Boolean)[0] || 'dashboard';
      const res = await axios.get(`${API}/themes/active`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page_key: pageKey },
        timeout: 4000,
      });
      const t = res.data?.theme;
      if (t) {
        setTheme(t);
        if (!previewRef.current) applyThemeToDocument(t);
      } else {
        document.documentElement.setAttribute('data-theme', 'islamic_royal');
      }
    } catch (e) {
      // On any error, ensure UI still renders with default theme
      document.documentElement.setAttribute('data-theme', 'islamic_royal');
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply preview locally without persisting (for the admin theme editor)
  const applyPreview = useCallback((previewTheme) => {
    previewRef.current = previewTheme;
    applyThemeToDocument(previewTheme);
  }, []);

  const clearPreview = useCallback(() => {
    previewRef.current = null;
    if (theme) applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    refresh();
  }, [location.pathname, refresh]);

  useEffect(() => {
    refresh();
    // Listen for cross-tab theme changes
    const onStorage = (e) => {
      if (e.key === 'theme:refresh') refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, loading, refresh, applyPreview, clearPreview }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
